import { useEffect } from 'react';
import { BASE } from './api.js';

// Connects to the SSE /events stream and dispatches request.updated /
// request.activity events into applySSEEvent. Auto-reconnects with capped
// exponential backoff; gives up after MAX_FAILURES consecutive failures.
export function useLiveEvents(applySSEEvent) {
  useEffect(() => {
    let delay = 1000;
    let retryTimer;
    let failCount = 0;
    let abortCtrl;
    const MAX_FAILURES = 3;

    async function connect() {
      abortCtrl = new AbortController();
      try {
        const res = await fetch(`${BASE}/events?topics=requests,notifications`, {
          credentials: 'include',
          signal: abortCtrl.signal,
        });
        if (!res.ok) throw new Error(`SSE ${res.status}`);
        delay = 1000; failCount = 0;
        const reader = res.body.getReader();
        const dec = new TextDecoder();
        let buf = '';
        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          buf += dec.decode(value, { stream: true });
          const blocks = buf.split('\n\n');
          buf = blocks.pop();
          for (const block of blocks) {
            let type = 'message', data = '';
            for (const line of block.split('\n')) {
              if (line.startsWith('event:')) type = line.slice(6).trim();
              else if (line.startsWith('data:')) data += line.slice(5).trim();
            }
            if (type === 'request.updated' || type === 'request.activity') {
              try { applySSEEvent(type, JSON.parse(data)); } catch {}
            }
          }
        }
        retryTimer = setTimeout(connect, 1000);
      } catch (e) {
        if (e.name === 'AbortError') return;
        failCount++;
        if (failCount >= MAX_FAILURES) return;
        retryTimer = setTimeout(connect, delay);
        delay = Math.min(delay * 2, 30000);
      }
    }
    connect();
    return () => { abortCtrl?.abort(); clearTimeout(retryTimer); };
  }, [applySSEEvent]);
}
